import { useCallback } from 'react';
import { useCheckFileCabinetStorageMutation } from 'redux-store/api/file-cabinet';
import { useGetCurrentPaymentPlanQuery } from 'redux-store/api/billing';
import { billingApi } from 'redux-store/api/billing/billing.api';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';

export class StorageLimitExceededError extends Error {
  readonly currentCapacityGb: number;
  readonly maxCapacityGb: number;

  constructor(currentCapacityGb: number, maxCapacityGb: number, message?: string) {
    super(
      message ||
        `You have reached the storage limit (${maxCapacityGb} GB) for your current plan.`
    );
    this.name = 'StorageLimitExceededError';
    this.currentCapacityGb = currentCapacityGb;
    this.maxCapacityGb = maxCapacityGb;
  }
}

/**
 * Authoritative BE storage check before Firebase/network upload.
 * Does not block on cached Billing usage (that goes stale after Trash deletes).
 */
export const useStorageUploadGuard = () => {
  const dispatch = useAppDispatch();
  const { data: currentPaymentPlan } = useGetCurrentPaymentPlanQuery();
  const [checkStorage] = useCheckFileCabinetStorageMutation();

  const assertCanUploadBytes = useCallback(
    async (additionalBytes: number): Promise<void> => {
      // Refresh plan usage so Billing UI / alert modal stay in sync after deletes.
      const planResult = await dispatch(
        billingApi.endpoints.getCurrentPaymentPlan.initiate(undefined, {
          forceRefetch: true,
        })
      );
      const plan =
        'data' in planResult ? planResult.data : currentPaymentPlan;

      const usedGb = plan?.activeStorageAmount;
      const maxGb = plan?.maxStorageAmount;

      try {
        await checkStorage({ size: String(additionalBytes) }).unwrap();
      } catch (error: any) {
        const message =
          error?.data?.message ||
          error?.message ||
          'You have reached the storage limit for your current plan.';

        throw new StorageLimitExceededError(
          typeof usedGb === 'number' ? usedGb : 0,
          typeof maxGb === 'number' ? maxGb : 0,
          Array.isArray(message) ? message.join(', ') : message
        );
      }
    },
    [checkStorage, currentPaymentPlan, dispatch]
  );

  return {
    assertCanUploadBytes,
    activeStorageAmountGb: currentPaymentPlan?.activeStorageAmount,
    maxStorageAmountGb: currentPaymentPlan?.maxStorageAmount,
  };
};
